'use client';

import { useSyncedLocalStorage, writeSyncedLocalStorage } from '@/lib/syncedLocalStorage';
import { AGENT_MODELS, type AgentModel } from '@/lib/agentModels';

// Admin-only per-request model pin: when set, every job request carries
// `model` so the whole stack (main agent, SDK subagents, merit side-agent)
// runs on the picked model instead of the server default. Mirrors the
// usePromptVersion localStorage pattern — per-browser, rides on every turn
// sent from this browser. The backend strips the param for non-admins.
/** null = use the server's default model. */
export type AgentModelSelection = AgentModel | null;

const AGENT_MODEL_KEY = 'photonium_agent_model';
const AGENT_MODEL_CHANGED_EVENT = 'agentModelChanged';

const VALID = new Set<string>(AGENT_MODELS);

function readStored(): AgentModelSelection {
  if (typeof window === 'undefined') return null;
  const v = localStorage.getItem(AGENT_MODEL_KEY);
  return v && VALID.has(v) ? (v as AgentModel) : null;
}

export function useAgentModel() {
  const model = useSyncedLocalStorage(
    AGENT_MODEL_KEY,
    AGENT_MODEL_CHANGED_EVENT,
    readStored,
  );

  function setModel(m: AgentModelSelection): void {
    writeSyncedLocalStorage(AGENT_MODEL_KEY, AGENT_MODEL_CHANGED_EVENT, m);
  }

  return { model, setModel };
}

export function getAgentModel(): AgentModelSelection {
  return readStored();
}
